import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Trophy, Star, Medal, Award, Target, Zap, Crown, Sparkles,
  Play, Pause, RotateCcw, Users, Timer, CheckCircle2, XCircle,
  ChevronRight, Loader2, Plus, Settings, GraduationCap, Brain,
  User, UsersRound, School, Search, X, Swords, Shield, Flame,
  ArrowRight, Clock, Eye, Save, GitBranch, LayoutGrid, TrendingUp,
  BarChart3, Percent, Calendar, Trash2
} from 'lucide-react';
import api from '../lib/api';
import BancoDeBatalhas from './BancoDeBatalhas';

// Níveis de dificuldade com valores
const NIVEIS = [
  { nivel: 1, valor: 100, dificuldade: 'Fácil', cor: 'bg-green-500' },
  { nivel: 2, valor: 200, dificuldade: 'Fácil', cor: 'bg-green-500' },
  { nivel: 3, valor: 300, dificuldade: 'Fácil', cor: 'bg-green-500' },
  { nivel: 4, valor: 500, dificuldade: 'Médio', cor: 'bg-yellow-500' },
  { nivel: 5, valor: 1000, dificuldade: 'Médio', cor: 'bg-yellow-500' },
  { nivel: 6, valor: 2000, dificuldade: 'Médio', cor: 'bg-yellow-500' },
  { nivel: 7, valor: 4000, dificuldade: 'Difícil', cor: 'bg-orange-500' },
  { nivel: 8, valor: 8000, dificuldade: 'Difícil', cor: 'bg-orange-500' },
  { nivel: 9, valor: 16000, dificuldade: 'Difícil', cor: 'bg-red-500' },
  { nivel: 10, valor: 32000, dificuldade: 'Expert', cor: 'bg-purple-500' },
];

const DISCIPLINAS = [
  'Português', 'Matemática', 'Ciências', 'História', 'Geografia',
  'Inglês', 'Artes', 'Educação Física', 'Conhecimentos Gerais',
];

// Modos de batalha
const MODOS_BATALHA = [
  { id: 'aluno_vs_aluno', label: 'Aluno vs Aluno', icon: User, desc: 'Duelo individual entre 2 alunos', minJogadores: 2 },
  { id: 'turma_vs_turma', label: 'Turma vs Turma', icon: School, desc: 'Batalha entre turmas completas', minJogadores: 2 },
  { id: 'time_vs_time', label: 'Time vs Time', icon: UsersRound, desc: 'Times personalizados se enfrentam', minJogadores: 4 },
  { id: 'batalha_royale', label: 'Batalha Royale', icon: Crown, desc: 'Todos contra todos, só um vence', minJogadores: 3 },
];

const QuizMilionario = () => {
  const [activeTab, setActiveTab] = useState('configurar');
  const [turmas, setTurmas] = useState([]);
  const [alunosPorTurma, setAlunosPorTurma] = useState({});
  const [loading, setLoading] = useState(false);
  const [gerando, setGerando] = useState(false);
  
  // Estado do jogo
  const [jogoAtivo, setJogoAtivo] = useState(false);
  const [rodadaAtual, setRodadaAtual] = useState(0);
  const [perguntaAtual, setPerguntaAtual] = useState(0);
  const [perguntas, setPerguntas] = useState([]);
  const [respostaSelecionada, setRespostaSelecionada] = useState(null);
  const [mostrarResultado, setMostrarResultado] = useState(false);
  const [tempoRestante, setTempoRestante] = useState(30);
  const [timerAtivo, setTimerAtivo] = useState(false);
  const [jogoFinalizado, setJogoFinalizado] = useState(false);
  const [vezDeQuem, setVezDeQuem] = useState(0); // Índice do jogador/time atual
  const [historico, setHistorico] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [historicoPartidas, setHistoricoPartidas] = useState([]);
  const [loadingRanking, setLoadingRanking] = useState(false);
  
  // Configurações
  const [configJogo, setConfigJogo] = useState({
    disciplina: '',
    tema: '',
    numPerguntas: 10,
    tempoResposta: 30,
    modoBatalha: 'aluno_vs_aluno',
    participantes: [], // { id, nome, tipo: 'aluno'|'turma'|'time', turma_id?, alunos?: [], pontos: 0, acertos: 0 }
  });

  const [turmaSelecionada, setTurmaSelecionada] = useState('');
  const [buscaAluno, setBuscaAluno] = useState('');
  const [showAddTimeModal, setShowAddTimeModal] = useState(false);
  const [novoTime, setNovoTime] = useState({ nome: '', turma_id: '', alunos: [] });

  // Carregar turmas
  useEffect(() => {
    const loadTurmas = async () => {
      try {
        const response = await api.get('/class-groups');
        const turmasOrdenadas = (response.data || []).sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );
        setTurmas(turmasOrdenadas);
      } catch (error) {
        console.error('Erro ao carregar turmas:', error);
      }
    };
    loadTurmas();
  }, []);

  // Carregar ranking e histórico quando aba mudar
  useEffect(() => {
    const loadRankingData = async () => {
      if (activeTab !== 'ranking') return;
      
      setLoadingRanking(true);
      try {
        const [rankingRes, historicoRes] = await Promise.all([
          api.get('/quiz/ranking?limit=20'),
          api.get('/quiz/historico?limit=20'),
        ]);
        setRanking(rankingRes.data || []);
        setHistoricoPartidas(historicoRes.data || []);
      } catch (error) {
        console.error('Erro ao carregar ranking:', error);
      } finally {
        setLoadingRanking(false);
      }
    };
    loadRankingData();
  }, [activeTab]);

  // Carregar alunos da turma
  const carregarAlunosTurma = async (turmaId) => {
    if (alunosPorTurma[turmaId]) return alunosPorTurma[turmaId];
    
    setLoading(true);
    try {
      const response = await api.get(`/students?class_id=${turmaId}`);
      const alunos = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setAlunosPorTurma(prev => ({ ...prev, [turmaId]: alunos }));
      return alunos;
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Timer do jogo
  useEffect(() => {
    let interval;
    if (timerAtivo && tempoRestante > 0) {
      interval = setInterval(() => {
        setTempoRestante(prev => prev - 1);
      }, 1000);
    } else if (tempoRestante === 0 && jogoAtivo && !mostrarResultado) {
      handleResposta(null);
    }
    return () => clearInterval(interval);
  }, [timerAtivo, tempoRestante, jogoAtivo, mostrarResultado]);

  const gerarPerguntas = async () => {
    if (!configJogo.disciplina) {
      toast.error('Selecione uma disciplina');
      return;
    }
    if (configJogo.participantes.length < 2) {
      toast.error('Adicione pelo menos 2 participantes');
      return;
    }

    // Identificar a turma/série dos participantes para gerar perguntas adequadas
    let turmasIds = configJogo.participantes
      .filter(p => p.turma_id)
      .map(p => p.turma_id);
    
    // Se não encontrou turma_id nos participantes, usar a turma selecionada
    if (turmasIds.length === 0 && turmaSelecionada) {
      turmasIds = [turmaSelecionada];
    }
    
    // Pegar a primeira turma como referência (todas devem ser do mesmo nível)
    const turmaRef = turmasIds.length > 0 ? turmas.find(t => t.id === turmasIds[0]) : null;
    const nomeTurma = turmaRef?.name || '';
    
    // Extrair série/ano da turma (ex: "1º ANO A - E.F1 - 2026" -> "1º ANO", "Fundamental I")
    let serie = '';
    let segmento = '';
    let faixaEtaria = '';
    
    if (nomeTurma) {
      // Detectar série
      const matchSerie = nomeTurma.match(/(\d+)[ºª]?\s*(ANO|ano|Ano)/i);
      if (matchSerie) {
        serie = `${matchSerie[1]}º Ano`;
      }
      
      // Detectar segmento
      if (nomeTurma.includes('E.I') || nomeTurma.toLowerCase().includes('infantil')) {
        segmento = 'Educação Infantil';
        faixaEtaria = '4 a 6 anos';
      } else if (nomeTurma.includes('E.F1') || (matchSerie && parseInt(matchSerie[1]) <= 5)) {
        segmento = 'Ensino Fundamental I (Anos Iniciais)';
        faixaEtaria = '6 a 10 anos';
      } else if (nomeTurma.includes('E.F2') || (matchSerie && parseInt(matchSerie[1]) >= 6 && parseInt(matchSerie[1]) <= 9)) {
        segmento = 'Ensino Fundamental II (Anos Finais)';
        faixaEtaria = '11 a 14 anos';
      } else if (nomeTurma.includes('E.M') || nomeTurma.toLowerCase().includes('médio')) {
        segmento = 'Ensino Médio';
        faixaEtaria = '15 a 17 anos';
      }
    }

    if (!serie && !segmento) {
      toast.error('Não foi possível identificar a série da turma. Selecione participantes de uma turma válida.');
      return;
    }

    setGerando(true);
    try {
      const systemPrompt = `Você é um mestre de quiz escolar especializado na BNCC (Base Nacional Comum Curricular).
Você DEVE criar perguntas EXCLUSIVAMENTE para alunos do ${serie} do ${segmento} (faixa etária: ${faixaEtaria}).

TURMA SELECIONADA: ${nomeTurma}
DISCIPLINA: ${configJogo.disciplina}
${configJogo.tema ? `TEMA ESPECÍFICO: ${configJogo.tema}` : ''}

REGRAS OBRIGATÓRIAS DA BNCC:
1. As perguntas DEVEM estar alinhadas com as habilidades e competências da BNCC para o ${serie} do ${segmento}
2. O vocabulário e complexidade DEVEM ser adequados para alunos de ${faixaEtaria}
3. NÃO faça perguntas de séries anteriores ou posteriores
4. Use linguagem apropriada para a idade
5. Os conteúdos devem corresponder ao currículo esperado para essa série

FORMATO:
- ${configJogo.numPerguntas} perguntas de múltipla escolha
- 4 alternativas por pergunta (A, B, C, D)
- Apenas 1 alternativa correta
- Dificuldade progressiva (fácil → médio → difícil)
- Explicação educativa para cada resposta

HABILIDADES BNCC para ${configJogo.disciplina} no ${serie}:
${getHabilidadesBNCC(configJogo.disciplina, serie, segmento)}

Retorne SOMENTE um JSON válido:
[
  {
    "pergunta": "Texto da pergunta adequada para ${serie}?",
    "alternativas": ["Opção A", "Opção B", "Opção C", "Opção D"],
    "correta": 0,
    "explicacao": "Explicação educativa da resposta",
    "habilidade_bncc": "Código da habilidade BNCC relacionada"
  }
]

"correta" = índice (0-3) da alternativa correta.`;

      const response = await api.post('/ai/generate', {
        system_prompt: systemPrompt,
        user_prompt: `Gere ${configJogo.numPerguntas} perguntas de ${configJogo.disciplina} para alunos do ${serie} do ${segmento}, seguindo a BNCC. Turma: ${nomeTurma}.`,
      }, { timeout: 120000 }); // 2 minutos de timeout

      const content = response.data.content || '';
      let perguntasGeradas = [];
      
      try {
        perguntasGeradas = JSON.parse(content);
      } catch {
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          perguntasGeradas = JSON.parse(jsonMatch[0]);
        }
      }

      if (Array.isArray(perguntasGeradas) && perguntasGeradas.length > 0) {
        const perguntasValidas = perguntasGeradas.map((p, idx) => ({
          id: idx + 1,
          pergunta: p.pergunta || `Pergunta ${idx + 1}`,
          alternativas: p.alternativas || ['A', 'B', 'C', 'D'],
          correta: typeof p.correta === 'number' ? p.correta : 0,
          explicacao: p.explicacao || '',
          habilidade_bncc: p.habilidade_bncc || '',
        }));
        
        setPerguntas(perguntasValidas);
        toast.success(`${perguntasValidas.length} perguntas geradas para ${serie} - ${segmento}!`);
        setActiveTab('jogar');
      } else {
        toast.error('Erro ao gerar perguntas. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao gerar perguntas: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGerando(false);
    }
  };

  // Função para retornar habilidades BNCC por disciplina e série
  const getHabilidadesBNCC = (disciplina, serie, segmento) => {
    const ano = parseInt(serie) || 1;
    
    const habilidades = {
      'Português': {
        'Fundamental I': {
          1: 'EF01LP01-08: Reconhecer que textos são lidos da esquerda para direita. Identificar letras do alfabeto. Relacionar elementos sonoros com representação escrita.',
          2: 'EF02LP01-10: Ler e compreender textos curtos. Identificar rimas e aliterações. Segmentar palavras em sílabas.',
          3: 'EF03LP01-12: Ler e compreender textos de gêneros variados. Identificar substantivos e adjetivos. Usar pontuação.',
          4: 'EF04LP01-15: Compreender textos de média extensão. Identificar verbos e pronomes. Produzir textos narrativos.',
          5: 'EF05LP01-20: Compreender textos complexos. Identificar figuras de linguagem simples. Produzir diferentes gêneros.',
        },
        'Fundamental II': {
          6: 'EF06LP01-15: Identificar tipos textuais. Reconhecer variedades linguísticas. Analisar estrutura narrativa.',
          7: 'EF07LP01-15: Analisar argumentação. Identificar figuras de linguagem. Compreender textos jornalísticos.',
          8: 'EF08LP01-15: Analisar textos publicitários. Compreender linguagem figurada. Produzir textos dissertativos.',
          9: 'EF09LP01-15: Analisar textos literários clássicos. Compreender intertextualidade. Produzir textos complexos.',
        }
      },
      'Matemática': {
        'Fundamental I': {
          1: 'EF01MA01-10: Contagem até 100. Comparação de quantidades. Figuras geométricas planas básicas.',
          2: 'EF02MA01-12: Adição e subtração até 100. Medidas de tempo e comprimento. Gráficos simples.',
          3: 'EF03MA01-15: Multiplicação e divisão básicas. Frações simples. Perímetro de figuras.',
          4: 'EF04MA01-18: Operações com números maiores. Frações equivalentes. Área de retângulos.',
          5: 'EF05MA01-20: Números decimais. Porcentagem básica. Volume de paralelepípedos.',
        },
        'Fundamental II': {
          6: 'EF06MA01-20: Números inteiros. Razão e proporção. Equações de 1º grau simples.',
          7: 'EF07MA01-20: Números racionais. Equações e inequações. Proporcionalidade.',
          8: 'EF08MA01-20: Potenciação e radiciação. Equações de 2º grau. Teorema de Pitágoras.',
          9: 'EF09MA01-20: Funções. Probabilidade. Estatística. Geometria analítica básica.',
        }
      },
      'Ciências': {
        'Fundamental I': {
          1: 'EF01CI01-06: Corpo humano básico. Materiais do cotidiano. Sol e suas características.',
          2: 'EF02CI01-08: Plantas e animais. Propriedades dos materiais. Importância da água.',
          3: 'EF03CI01-09: Produção de som. Efeitos da luz. Características da Terra.',
          4: 'EF04CI01-11: Misturas. Transformações da matéria. Cadeia alimentar.',
          5: 'EF05CI01-13: Propriedades da matéria. Nutrição e saúde. Ciclo hidrológico.',
        },
        'Fundamental II': {
          6: 'EF06CI01-14: Misturas homogêneas e heterogêneas. Célula como unidade da vida. Rochas e solo.',
          7: 'EF07CI01-16: Máquinas simples. Ecossistemas. Atmosfera terrestre.',
          8: 'EF08CI01-16: Reprodução. Sexualidade. Fontes de energia. Clima.',
          9: 'EF09CI01-17: Composição química. Hereditariedade. Evolução. Preservação ambiental.',
        }
      },
      'História': {
        'Fundamental I': {
          1: 'EF01HI01-06: Identidade pessoal e familiar. Noção de tempo. Comunidade escolar.',
          2: 'EF02HI01-08: Relações familiares. Diversidade cultural. Lugares de vivência.',
          3: 'EF03HI01-09: Cidade e campo. Povos indígenas. Patrimônio histórico.',
          4: 'EF04HI01-11: Povos africanos. Descobrimento do Brasil. Transformações urbanas.',
          5: 'EF05HI01-10: Cidadania. Direitos humanos. Povos e culturas no Brasil.',
        },
        'Fundamental II': {
          6: 'EF06HI01-17: Pré-história. Civilizações antigas. Grécia e Roma.',
          7: 'EF07HI01-17: Idade Média. Feudalismo. Renascimento. Grandes navegações.',
          8: 'EF08HI01-19: Independência do Brasil. Revoluções. Abolição da escravidão.',
          9: 'EF09HI01-20: República brasileira. Guerras mundiais. Democracia e cidadania.',
        }
      },
      'Geografia': {
        'Fundamental I': {
          1: 'EF01GE01-07: Orientação espacial. Pontos de referência. Lugares de convivência.',
          2: 'EF02GE01-08: Bairro e cidade. Meios de transporte. Riscos naturais.',
          3: 'EF03GE01-09: Paisagem natural e cultural. Agricultura e pecuária. Matérias-primas.',
          4: 'EF04GE01-11: Território brasileiro. Regiões do Brasil. Relevo e clima.',
          5: 'EF05GE01-12: Diversidade cultural. Migração. Questões ambientais.',
        },
        'Fundamental II': {
          6: 'EF06GE01-13: Identidade sociocultural. Paisagem. Mundo do trabalho.',
          7: 'EF07GE01-12: Formação territorial brasileira. Biomas. Urbanização.',
          8: 'EF08GE01-14: Geopolítica mundial. América Latina. Globalização.',
          9: 'EF09GE01-18: Europa, Ásia e África. Conflitos mundiais. Meio ambiente global.',
        }
      }
    };

    const segmentoKey = segmento.includes('Fundamental I') ? 'Fundamental I' : 
                        segmento.includes('Fundamental II') ? 'Fundamental II' : 'Fundamental I';
    
    return habilidades[disciplina]?.[segmentoKey]?.[ano] || 
           `Habilidades da BNCC para ${disciplina} no ${serie} do ${segmento}. Conteúdo adequado para a faixa etária.`;
  };

  const iniciarJogo = () => {
    if (perguntas.length === 0) {
      toast.error('Gere as perguntas primeiro!');
      return;
    }
    if (configJogo.participantes.length < 2) {
      toast.error('Adicione pelo menos 2 participantes!');
      return;
    }

    // Resetar pontos dos participantes
    setConfigJogo(prev => ({
      ...prev,
      participantes: prev.participantes.map(p => ({ ...p, pontos: 0, acertos: 0 }))
    }));

    setJogoAtivo(true);
    setJogoFinalizado(false);
    setPerguntaAtual(0);
    setRodadaAtual(1);
    setVezDeQuem(0);
    setRespostaSelecionada(null);
    setMostrarResultado(false);
    setTempoRestante(configJogo.tempoResposta);
    setTimerAtivo(true);
    setHistorico([]);
  };

  const handleResposta = (index) => {
    setTimerAtivo(false);
    setRespostaSelecionada(index);
    setMostrarResultado(true);

    const pergunta = perguntas[perguntaAtual];
    const estaCorreta = index === pergunta.correta;
    const participanteAtual = configJogo.participantes[vezDeQuem];
    const valorNivel = NIVEIS[Math.min(perguntaAtual, NIVEIS.length - 1)]?.valor || 100;
    
    // Atualizar pontuação
    setConfigJogo(prev => ({
      ...prev,
      participantes: prev.participantes.map((p, idx) => {
        if (idx !== vezDeQuem) return p;
        return {
          ...p,
          pontos: p.pontos + (estaCorreta ? valorNivel : 0),
          acertos: p.acertos + (estaCorreta ? 1 : 0),
        };
      })
    }));

    // Adicionar ao histórico
    setHistorico(prev => [...prev, {
      rodada: rodadaAtual,
      pergunta: perguntaAtual + 1,
      participante: participanteAtual.nome,
      correta: estaCorreta,
      pontos: estaCorreta ? valorNivel : 0,
    }]);

    if (estaCorreta) {
      toast.success(`${participanteAtual.nome}: +${valorNivel.toLocaleString('pt-BR')} pts!`);
    } else {
      toast.error(`${participanteAtual.nome}: ${index === null ? 'Tempo esgotado!' : 'Errou!'}`);
    }
  };

  const proximaRodada = async () => {
    const proximoJogador = (vezDeQuem + 1) % configJogo.participantes.length;
    const novaRodada = proximoJogador === 0 ? rodadaAtual + 1 : rodadaAtual;
    const proximaPergunta = perguntaAtual + 1;

    if (proximaPergunta >= perguntas.length) {
      // Fim do jogo - salvar resultado no banco de dados
      setJogoAtivo(false);
      setJogoFinalizado(true);
      setTimerAtivo(false);
      
      // Encontrar vencedor
      const classificacaoFinal = [...configJogo.participantes].sort((a, b) => {
        if (b.pontos !== a.pontos) return b.pontos - a.pontos;
        return b.acertos - a.acertos;
      });
      const vencedor = classificacaoFinal[0];
      
      // Salvar no banco de dados
      try {
        await api.post('/quiz/batalhas', {
          disciplina: configJogo.disciplina,
          tema: configJogo.tema || null,
          modo_batalha: configJogo.modoBatalha,
          participantes: configJogo.participantes.map(p => ({
            id: p.id,
            nome: p.nome,
            tipo: p.tipo,
            turma_id: p.turma_id || null,
            pontos: p.pontos,
            acertos: p.acertos,
          })),
          num_perguntas: perguntas.length,
          tempo_resposta: configJogo.tempoResposta,
          vencedor_id: vencedor?.id,
          vencedor_nome: vencedor?.nome,
        });
        toast.success('Resultado salvo no Hall da Fama!');
      } catch (error) {
        console.error('Erro ao salvar resultado:', error);
      }
      
      return;
    }

    setVezDeQuem(proximoJogador);
    setRodadaAtual(novaRodada);
    setPerguntaAtual(proximaPergunta);
    setRespostaSelecionada(null);
    setMostrarResultado(false);
    setTempoRestante(configJogo.tempoResposta);
    setTimerAtivo(true);
  };

  const reiniciarJogo = () => {
    setJogoAtivo(false);
    setJogoFinalizado(false);
    setPerguntaAtual(0);
    setRodadaAtual(0);
    setVezDeQuem(0);
    setRespostaSelecionada(null);
    setMostrarResultado(false);
    setTimerAtivo(false);
  };

  // Adicionar participante
  const adicionarAluno = (aluno, turmaId) => {
    const turma = turmas.find(t => t.id === turmaId);
    const participante = {
      id: `aluno_${aluno.id}`,
      nome: aluno.name,
      tipo: 'aluno',
      turma_id: turmaId,
      turma_nome: turma?.name,
      pontos: 0,
      acertos: 0,
    };
    
    if (configJogo.participantes.find(p => p.id === participante.id)) {
      toast.error('Aluno já adicionado');
      return;
    }

    setConfigJogo(prev => ({
      ...prev,
      participantes: [...prev.participantes, participante]
    }));
    toast.success(`${aluno.name} adicionado!`);
  };

  const adicionarTurma = (turma) => {
    const participante = {
      id: `turma_${turma.id}`,
      nome: turma.name,
      tipo: 'turma',
      turma_id: turma.id,
      pontos: 0,
      acertos: 0,
    };
    
    if (configJogo.participantes.find(p => p.id === participante.id)) {
      toast.error('Turma já adicionada');
      return;
    }

    setConfigJogo(prev => ({
      ...prev,
      participantes: [...prev.participantes, participante]
    }));
    toast.success(`${turma.name} adicionada!`);
  };

  const adicionarTime = () => {
    if (!novoTime.nome || novoTime.alunos.length === 0) {
      toast.error('Preencha o nome e selecione os alunos');
      return;
    }

    const participante = {
      id: `time_${Date.now()}`,
      nome: novoTime.nome,
      tipo: 'time',
      turma_id: novoTime.turma_id || turmaSelecionada, // Associar turma ao time
      alunos: novoTime.alunos,
      pontos: 0,
      acertos: 0,
    };

    setConfigJogo(prev => ({
      ...prev,
      participantes: [...prev.participantes, participante]
    }));

    setNovoTime({ nome: '', turma_id: '', alunos: [] });
    setShowAddTimeModal(false);
    toast.success(`Time "${novoTime.nome}" criado!`);
  };

  const removerParticipante = (id) => {
    setConfigJogo(prev => ({
      ...prev,
      participantes: prev.participantes.filter(p => p.id !== id)
    }));
  };

  const toggleAlunoTime = (alunoId) => {
    setNovoTime(prev => ({
      ...prev,
      alunos: prev.alunos.includes(alunoId)
        ? prev.alunos.filter(id => id !== alunoId)
        : [...prev.alunos, alunoId]
    }));
  };

  const getAlunoNome = (alunoId) => {
    for (const alunos of Object.values(alunosPorTurma)) {
      const aluno = alunos.find(a => a.id === alunoId);
      if (aluno) return aluno.name;
    }
    return 'Jogador';
  };

  // Classificação final
  const classificacao = [...configJogo.participantes].sort((a, b) => {
    if (b.pontos !== a.pontos) return b.pontos - a.pontos;
    return b.acertos - a.acertos;
  });

  const pergunta = perguntas[perguntaAtual];
  const nivelAtual = NIVEIS[Math.min(perguntaAtual, NIVEIS.length - 1)];
  const participanteAtual = configJogo.participantes[vezDeQuem];
  const modoBatalha = MODOS_BATALHA.find(m => m.id === configJogo.modoBatalha);
  const ModoIcon = modoBatalha?.icon || Users;

  // Filtrar alunos: remover os que já foram selecionados como participantes
  const alunosFiltrados = turmaSelecionada && alunosPorTurma[turmaSelecionada]
    ? alunosPorTurma[turmaSelecionada].filter(a => {
        const matchBusca = a.name?.toLowerCase().includes(buscaAluno.toLowerCase());
        const jaAdicionado = configJogo.participantes.some(p => p.id === `aluno_${a.id}`);
        return matchBusca && !jaAdicionado;
      })
    : [];

  // Função para excluir batalha do histórico
  const handleExcluirBatalha = async (batalhaId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta batalha? Os pontos serão removidos do ranking.')) {
      return;
    }
    
    try {
      await api.delete(`/quiz/batalhas/${batalhaId}`);
      toast.success('Batalha excluída com sucesso!');
      // Recarregar ranking e histórico
      const [rankingRes, historicoRes] = await Promise.all([
        api.get('/quiz/ranking?limit=20'),
        api.get('/quiz/historico?limit=20'),
      ]);
      setRanking(rankingRes.data || []);
      setHistoricoPartidas(historicoRes.data || []);
    } catch (error) {
      console.error('Erro ao excluir batalha:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir batalha');
    }
  };

  // Função para excluir participante do ranking
  const handleExcluirDoRanking = async (participanteId, nome) => {
    if (!window.confirm(`Tem certeza que deseja remover "${nome}" do ranking? Esta ação não pode ser desfeita.`)) {
      return;
    }
    
    try {
      await api.delete(`/quiz/ranking/${participanteId}`);
      toast.success(`${nome} removido do ranking!`);
      // Recarregar ranking
      const rankingRes = await api.get('/quiz/ranking?limit=20');
      setRanking(rankingRes.data || []);
    } catch (error) {
      console.error('Erro ao excluir do ranking:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir do ranking');
    }
  };

  // Função para limpar todo o ranking
  const handleLimparRanking = async () => {
    if (!window.confirm('⚠️ ATENÇÃO: Tem certeza que deseja LIMPAR TODO O RANKING?\n\nTodas as pontuações e estatísticas serão removidas permanentemente!')) {
      return;
    }
    
    try {
      const response = await api.delete('/quiz/ranking');
      toast.success(`Ranking limpo! ${response.data.registros_removidos} registros removidos.`);
      setRanking([]);
    } catch (error) {
      console.error('Erro ao limpar ranking:', error);
      toast.error(error.response?.data?.detail || 'Erro ao limpar ranking');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="quiz-title">
            <Swords className="w-7 h-7 text-red-500" />
            Desafios dos Campeões
          </h1>
          <p className="text-muted-foreground mt-1">
            Batalhas de conhecimento entre alunos, turmas e times!
          </p>
        </div>
        {perguntas.length > 0 && !jogoAtivo && (
          <Badge variant="outline" className="text-green-600 border-green-600 text-lg px-4 py-2">
            <CheckCircle2 className="w-5 h-5 mr-2" />
            {perguntas.length} perguntas prontas
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="configurar" className="gap-2" data-testid="tab-configurar">
            <Settings className="w-4 h-4" />
            Configurar Batalha
          </TabsTrigger>
          <TabsTrigger value="jogar" className="gap-2" data-testid="tab-jogar">
            <Swords className="w-4 h-4" />
            Arena
          </TabsTrigger>
          <TabsTrigger value="banco" className="gap-2" data-testid="tab-banco">
            <GitBranch className="w-4 h-4" />
            Banco de Batalhas
          </TabsTrigger>
          <TabsTrigger value="ranking" className="gap-2" data-testid="tab-ranking">
            <Trophy className="w-4 h-4" />
            Hall da Fama
          </TabsTrigger>
        </TabsList>

        <TabsContent value="banco" className="pt-3">
          <BancoDeBatalhas />
        </TabsContent>

        {/* Tab Configurar */}
        <TabsContent value="configurar" className="space-y-6">
          {/* Modo de Batalha */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Swords className="w-5 h-5 text-red-500" />
                Modo de Batalha
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {MODOS_BATALHA.map(modo => {
                  const Icon = modo.icon;
                  const isSelected = configJogo.modoBatalha === modo.id;
                  return (
                    <button
                      key={modo.id}
                      onClick={() => setConfigJogo({...configJogo, modoBatalha: modo.id, participantes: []})}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected 
                          ? 'border-red-500 bg-red-50 shadow-lg scale-[1.02]' 
                          : 'border-gray-200 hover:border-gray-300 hover:shadow'
                      }`}
                    >
                      <Icon className={`w-8 h-8 mb-2 ${isSelected ? 'text-red-600' : 'text-gray-500'}`} />
                      <p className={`font-bold ${isSelected ? 'text-red-700' : ''}`}>{modo.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{modo.desc}</p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configurações do Quiz */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Brain className="w-5 h-5 text-purple-600" />
                  Configurações do Quiz
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Disciplina *</Label>
                  <Select value={configJogo.disciplina} onValueChange={v => setConfigJogo({...configJogo, disciplina: v})}>
                    <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {DISCIPLINAS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tema específico (opcional)</Label>
                  <Input
                    value={configJogo.tema}
                    onChange={e => setConfigJogo({...configJogo, tema: e.target.value})}
                    placeholder="Ex: Revolução Industrial, Frações..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Perguntas</Label>
                    <Select value={configJogo.numPerguntas.toString()} onValueChange={v => setConfigJogo({...configJogo, numPerguntas: parseInt(v)})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 perguntas</SelectItem>
                        <SelectItem value="10">10 perguntas</SelectItem>
                        <SelectItem value="15">15 perguntas</SelectItem>
                        <SelectItem value="20">20 perguntas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tempo/Pergunta</Label>
                    <Select value={configJogo.tempoResposta.toString()} onValueChange={v => setConfigJogo({...configJogo, tempoResposta: parseInt(v)})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 segundos</SelectItem>
                        <SelectItem value="30">30 segundos</SelectItem>
                        <SelectItem value="45">45 segundos</SelectItem>
                        <SelectItem value="60">60 segundos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Indicação da turma/série para BNCC */}
                {(configJogo.participantes.length > 0 || turmaSelecionada) && (() => {
                  // Tentar obter turma dos participantes primeiro
                  let turmaInfo = null;
                  const turmaRef = configJogo.participantes.find(p => p.turma_id);
                  if (turmaRef) {
                    turmaInfo = turmas.find(t => t.id === turmaRef.turma_id);
                  }
                  // Fallback para turma selecionada
                  if (!turmaInfo && turmaSelecionada) {
                    turmaInfo = turmas.find(t => t.id === turmaSelecionada);
                  }
                  if (!turmaInfo) return null;
                  
                  const nomeTurma = turmaInfo.name || '';
                  let segmento = 'Fundamental';
                  if (nomeTurma.includes('E.I') || nomeTurma.toLowerCase().includes('infantil')) {
                    segmento = 'Educação Infantil';
                  } else if (nomeTurma.includes('E.F1')) {
                    segmento = 'Fundamental I';
                  } else if (nomeTurma.includes('E.F2')) {
                    segmento = 'Fundamental II';
                  } else if (nomeTurma.includes('E.M')) {
                    segmento = 'Ensino Médio';
                  }
                  
                  return (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-700">
                        <GraduationCap className="w-5 h-5" />
                        <span className="font-semibold">Perguntas baseadas na BNCC</span>
                      </div>
                      <p className="text-sm text-blue-600 mt-1">
                        As perguntas serão geradas especificamente para: <strong>{nomeTurma}</strong> ({segmento})
                      </p>
                    </div>
                  );
                })()}

                <Button 
                  onClick={gerarPerguntas} 
                  disabled={gerando || !configJogo.disciplina || configJogo.participantes.length < 2}
                  className="w-full gap-2 bg-purple-600 hover:bg-purple-700 text-lg py-6"
                >
                  {gerando ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />Gerando...</>
                  ) : (
                    <><Sparkles className="w-5 h-5" />Gerar Perguntas com IA</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Participantes */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ModoIcon className="w-5 h-5 text-blue-600" />
                    Participantes ({configJogo.participantes.length})
                  </CardTitle>
                  {configJogo.modoBatalha === 'time_vs_time' && (
                    <Button size="sm" variant="outline" onClick={() => setShowAddTimeModal(true)}>
                      <Plus className="w-4 h-4 mr-1" />Criar Time
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selecionar Turma - para todos os modos exceto turma_vs_turma */}
                {(configJogo.modoBatalha === 'aluno_vs_aluno' || configJogo.modoBatalha === 'batalha_royale' || configJogo.modoBatalha === 'time_vs_time') && (
                  <div className="space-y-2">
                    <Label>Selecione a Turma</Label>
                    <Select value={turmaSelecionada} onValueChange={v => { setTurmaSelecionada(v); carregarAlunosTurma(v); }}>
                      <SelectTrigger><SelectValue placeholder="Escolha a turma..." /></SelectTrigger>
                      <SelectContent>
                        {turmas.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Lista de Alunos */}
                {(configJogo.modoBatalha === 'aluno_vs_aluno' || configJogo.modoBatalha === 'batalha_royale') && turmaSelecionada && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input value={buscaAluno} onChange={e => setBuscaAluno(e.target.value)} placeholder="Buscar aluno..." className="pl-10" />
                    </div>
                    <div className="border rounded-lg max-h-40 overflow-y-auto">
                      {loading ? (
                        <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                      ) : alunosFiltrados.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground text-sm">Nenhum aluno</div>
                      ) : (
                        alunosFiltrados.map(aluno => (
                          <div 
                            key={aluno.id}
                            className="flex items-center justify-between p-2 border-b last:border-0 hover:bg-slate-50"
                          >
                            <span className="text-sm">{aluno.name}</span>
                            <Button size="sm" variant="ghost" onClick={() => adicionarAluno(aluno, turmaSelecionada)}>
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Turmas para modo turma vs turma */}
                {configJogo.modoBatalha === 'turma_vs_turma' && (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {turmas.map(turma => (
                      <Button
                        key={turma.id}
                        variant={configJogo.participantes.find(p => p.id === `turma_${turma.id}`) ? "default" : "outline"}
                        size="sm"
                        className="justify-start"
                        onClick={() => adicionarTurma(turma)}
                      >
                        <School className="w-4 h-4 mr-2" />
                        {turma.name}
                      </Button>
                    ))}
                  </div>
                )}

                {/* Participantes adicionados */}
                {configJogo.participantes.length > 0 && (
                  <div className="space-y-2">
                    <Label>Na Batalha:</Label>
                    <div className="flex flex-wrap gap-2">
                      {configJogo.participantes.map((p, idx) => (
                        <Badge key={p.id} variant="secondary" className="gap-1 py-1 px-3">
                          <span className="font-bold text-purple-600">#{idx + 1}</span>
                          {p.nome}
                          <button onClick={() => removerParticipante(p.id)} className="ml-1 hover:text-red-600">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {configJogo.participantes.length < 2 && (
                  <p className="text-sm text-amber-600 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Adicione pelo menos 2 participantes para batalhar!
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Jogar (Arena) */}
        <TabsContent value="jogar" className="space-y-4">
          {!jogoAtivo && !jogoFinalizado ? (
            <Card className="max-w-2xl mx-auto">
              <CardHeader className="text-center">
                <div className="w-28 h-28 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl animate-pulse">
                  <Swords className="w-14 h-14 text-white" />
                </div>
                <CardTitle className="text-3xl">Arena de Batalha</CardTitle>
                <CardDescription className="text-lg">
                  {perguntas.length > 0 
                    ? `${configJogo.participantes.length} competidores • ${perguntas.length} perguntas`
                    : 'Configure a batalha primeiro'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {perguntas.length > 0 && configJogo.participantes.length >= 2 && (
                  <>
                    <div className="bg-gradient-to-r from-red-100 to-orange-100 p-4 rounded-xl">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><p className="text-muted-foreground">Modo</p><p className="font-bold text-red-800">{modoBatalha?.label}</p></div>
                        <div><p className="text-muted-foreground">Disciplina</p><p className="font-bold text-red-800">{configJogo.disciplina}</p></div>
                      </div>
                      <div className="mt-4">
                        <p className="text-muted-foreground text-sm mb-2">Competidores:</p>
                        <div className="flex flex-wrap gap-2">
                          {configJogo.participantes.map((p, idx) => (
                            <Badge key={p.id} className="bg-white/50 text-red-800">
                              #{idx + 1} {p.nome}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={iniciarJogo} 
                      className="w-full gap-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-xl py-8 shadow-lg"
                    >
                      <Flame className="w-6 h-6 animate-pulse" />
                      INICIAR BATALHA!
                    </Button>
                  </>
                )}
                
                {(perguntas.length === 0 || configJogo.participantes.length < 2) && (
                  <Button variant="outline" onClick={() => setActiveTab('configurar')} className="w-full">
                    Ir para Configurações
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : jogoFinalizado ? (
            // Resultado Final
            <div className="max-w-3xl mx-auto space-y-6">
              <Card className="bg-gradient-to-br from-yellow-100 to-orange-100 border-2 border-yellow-400 shadow-xl">
                <CardContent className="p-8 text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Crown className="w-12 h-12 text-white" />
                  </div>
                  <h2 className="text-4xl font-bold text-yellow-800 mb-2">
                    🏆 CAMPEÃO 🏆
                  </h2>
                  <p className="text-5xl font-black text-orange-600 mb-2">
                    {classificacao[0]?.nome}
                  </p>
                  <p className="text-3xl font-bold text-yellow-600">
                    {classificacao[0]?.pontos.toLocaleString('pt-BR')} pontos
                  </p>
                </CardContent>
              </Card>

              {/* Classificação Final */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Medal className="w-5 h-5" />
                    Classificação Final
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {classificacao.map((p, idx) => (
                      <div key={p.id} className={`flex items-center gap-4 p-3 rounded-lg ${
                        idx === 0 ? 'bg-yellow-100 border-2 border-yellow-400' :
                        idx === 1 ? 'bg-slate-100 border-2 border-slate-300' :
                        idx === 2 ? 'bg-amber-100 border-2 border-amber-400' :
                        'bg-slate-50'
                      }`}>
                        <span className="text-2xl">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}º`}
                        </span>
                        <div className="flex-1">
                          <p className="font-bold">{p.nome}</p>
                          <p className="text-sm text-muted-foreground">{p.acertos} acertos</p>
                        </div>
                        <Badge className="text-lg px-3 py-1 bg-purple-600">
                          {p.pontos.toLocaleString('pt-BR')} pts
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-center gap-4">
                <Button onClick={reiniciarJogo} variant="outline" className="gap-2">
                  <RotateCcw className="w-4 h-4" />Revanche
                </Button>
                <Button onClick={() => setActiveTab('configurar')} className="gap-2">
                  <Settings className="w-4 h-4" />Nova Batalha
                </Button>
              </div>
            </div>
          ) : (
            // Jogo em andamento
            <div className="max-w-4xl mx-auto space-y-4">
              {/* Placar ao vivo */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                {configJogo.participantes.map((p, idx) => (
                  <Card key={p.id} className={`p-3 ${idx === vezDeQuem ? 'border-2 border-red-500 bg-red-50 shadow-lg' : ''}`}>
                    <div className="text-center">
                      <p className={`font-bold text-sm truncate ${idx === vezDeQuem ? 'text-red-700' : ''}`}>
                        {idx === vezDeQuem && '🎯 '}{p.nome}
                      </p>
                      <p className="text-xl font-black text-purple-600">{p.pontos.toLocaleString('pt-BR')}</p>
                      <p className="text-xs text-muted-foreground">{p.acertos} acertos</p>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Barra de Status */}
              <div className="bg-gradient-to-r from-purple-600 to-red-600 text-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xs opacity-80">Pergunta</p>
                      <p className="text-2xl font-bold">{perguntaAtual + 1}/{perguntas.length}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs opacity-80">Vez de</p>
                      <p className="text-lg font-bold text-yellow-300">{participanteAtual?.nome}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xs opacity-80">Vale</p>
                      <p className="text-xl font-bold text-yellow-300">{nivelAtual?.valor.toLocaleString('pt-BR')} pts</p>
                    </div>
                    <div className={`text-center px-4 py-2 rounded-lg ${tempoRestante <= 10 ? 'bg-red-500 animate-pulse' : 'bg-white/20'}`}>
                      <Timer className="w-5 h-5 mx-auto" />
                      <p className="text-2xl font-bold">{tempoRestante}s</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pergunta */}
              <Card className="border-2 border-purple-200 shadow-xl">
                <CardContent className="p-6">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Badge className={`${nivelAtual?.cor} text-white`}>{nivelAtual?.dificuldade}</Badge>
                  </div>
                  
                  <p className="text-xl font-medium text-center mb-8 leading-relaxed">
                    {pergunta?.pergunta}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pergunta?.alternativas.map((alt, index) => {
                      let bgColor = 'bg-slate-100 hover:bg-slate-200 border-2 border-transparent';
                      let textColor = 'text-slate-700';
                      
                      if (mostrarResultado) {
                        if (index === pergunta.correta) {
                          bgColor = 'bg-green-500 border-2 border-green-600';
                          textColor = 'text-white';
                        } else if (index === respostaSelecionada && index !== pergunta.correta) {
                          bgColor = 'bg-red-500 border-2 border-red-600';
                          textColor = 'text-white';
                        }
                      }

                      return (
                        <button
                          key={index}
                          onClick={() => !mostrarResultado && handleResposta(index)}
                          disabled={mostrarResultado}
                          className={`p-5 rounded-xl text-left transition-all ${bgColor} ${textColor} ${
                            !mostrarResultado ? 'hover:scale-[1.02] hover:shadow-lg' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                              mostrarResultado && index === pergunta.correta ? 'bg-white text-green-600' :
                              mostrarResultado && index === respostaSelecionada ? 'bg-white text-red-600' :
                              'bg-purple-600 text-white'
                            }`}>
                              {String.fromCharCode(65 + index)}
                            </span>
                            <span className="flex-1 pt-2 text-lg">{alt}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {mostrarResultado && pergunta?.explicacao && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-blue-800"><strong>💡 </strong>{pergunta.explicacao}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {mostrarResultado && (
                <div className="flex justify-center">
                  <Button onClick={proximaRodada} className="gap-2 bg-green-600 hover:bg-green-700 text-xl px-8 py-6">
                    {perguntaAtual < perguntas.length - 1 ? (
                      <>Próximo Jogador <ChevronRight className="w-6 h-6" /></>
                    ) : (
                      <><Trophy className="w-6 h-6" />Ver Resultado Final</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Tab Ranking/Hall da Fama */}
        <TabsContent value="ranking" className="space-y-6">
          {/* Estatísticas Gerais */}
          {ranking.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-8 h-8 opacity-80" />
                    <div>
                      <p className="text-2xl font-bold">{historicoPartidas.length}</p>
                      <p className="text-sm opacity-80">Batalhas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 opacity-80" />
                    <div>
                      <p className="text-2xl font-bold">{ranking.length}</p>
                      <p className="text-sm opacity-80">Competidores</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-8 h-8 opacity-80" />
                    <div>
                      <p className="text-2xl font-bold">
                        {ranking.reduce((a, r) => a + (r.total_pontos || 0), 0).toLocaleString('pt-BR')}
                      </p>
                      <p className="text-sm opacity-80">Pontos Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-8 h-8 opacity-80" />
                    <div>
                      <p className="text-2xl font-bold">
                        {ranking.reduce((a, r) => a + (r.total_acertos || 0), 0)}
                      </p>
                      <p className="text-sm opacity-80">Acertos Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Pódio Visual */}
          {ranking.length >= 3 && (
            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-400">
                  <Crown className="w-6 h-6" />
                  Pódio dos Campeões
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-center gap-4 py-6">
                  {/* 2º Lugar */}
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-slate-300 to-slate-400 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg border-4 border-slate-200">
                      <span className="text-3xl">🥈</span>
                    </div>
                    <div className="bg-slate-700 rounded-t-lg px-4 py-8 w-28">
                      <p className="font-bold text-sm truncate">{ranking[1]?.nome}</p>
                      <p className="text-yellow-400 text-lg font-bold">{ranking[1]?.total_pontos?.toLocaleString('pt-BR')}</p>
                      <p className="text-xs text-slate-400">{ranking[1]?.vitorias} vitórias</p>
                    </div>
                  </div>
                  
                  {/* 1º Lugar */}
                  <div className="text-center -mt-8">
                    <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-2 shadow-xl border-4 border-yellow-300 animate-pulse">
                      <span className="text-4xl">🥇</span>
                    </div>
                    <div className="bg-gradient-to-b from-yellow-600 to-yellow-700 rounded-t-lg px-4 py-12 w-32">
                      <Crown className="w-6 h-6 mx-auto mb-1 text-yellow-200" />
                      <p className="font-bold truncate">{ranking[0]?.nome}</p>
                      <p className="text-yellow-200 text-xl font-bold">{ranking[0]?.total_pontos?.toLocaleString('pt-BR')}</p>
                      <p className="text-xs text-yellow-300">{ranking[0]?.vitorias} vitórias</p>
                    </div>
                  </div>
                  
                  {/* 3º Lugar */}
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg border-4 border-amber-400">
                      <span className="text-3xl">🥉</span>
                    </div>
                    <div className="bg-amber-800 rounded-t-lg px-4 py-6 w-28">
                      <p className="font-bold text-sm truncate">{ranking[2]?.nome}</p>
                      <p className="text-yellow-400 text-lg font-bold">{ranking[2]?.total_pontos?.toLocaleString('pt-BR')}</p>
                      <p className="text-xs text-amber-300">{ranking[2]?.vitorias} vitórias</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Ranking Completo */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Medal className="w-5 h-5 text-yellow-500" />
                      Ranking Completo
                    </CardTitle>
                    <CardDescription>Classificação por pontuação total</CardDescription>
                  </div>
                  {ranking.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={handleLimparRanking}
                      data-testid="limpar-ranking-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Limpar Ranking
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loadingRanking ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600" />
                  </div>
                ) : ranking.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Trophy className="w-16 h-16 mx-auto mb-4 opacity-20" />
                    <p className="text-lg">Nenhuma batalha registrada</p>
                    <p className="text-sm mt-2">Jogue para aparecer no ranking!</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {ranking.map((item, idx) => {
                      const winRate = item.total_batalhas > 0 
                        ? Math.round((item.vitorias / item.total_batalhas) * 100) 
                        : 0;
                      const avgPoints = item.total_batalhas > 0 
                        ? Math.round(item.total_pontos / item.total_batalhas) 
                        : 0;
                      
                      return (
                        <div key={item.participante_id} className={`p-3 rounded-lg border-2 transition-all hover:shadow-md ${
                          idx === 0 ? 'bg-yellow-50 border-yellow-400' :
                          idx === 1 ? 'bg-slate-50 border-slate-300' :
                          idx === 2 ? 'bg-amber-50 border-amber-400' :
                          'bg-white border-slate-200'
                        }`}>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl w-10 text-center font-bold">
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}º`}
                            </span>
                            <div className="flex-1">
                              <p className="font-bold">{item.nome}</p>
                              <div className="grid grid-cols-4 gap-2 mt-1 text-xs">
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Swords className="w-3 h-3" />
                                  {item.total_batalhas}
                                </div>
                                <div className="flex items-center gap-1 text-green-600">
                                  <Trophy className="w-3 h-3" />
                                  {item.vitorias}
                                </div>
                                <div className="flex items-center gap-1 text-blue-600">
                                  <Percent className="w-3 h-3" />
                                  {winRate}%
                                </div>
                                <div className="flex items-center gap-1 text-purple-600">
                                  <BarChart3 className="w-3 h-3" />
                                  ~{avgPoints.toLocaleString('pt-BR')}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge className="bg-purple-600 text-lg px-3">
                                {item.total_pontos?.toLocaleString('pt-BR')}
                              </Badge>
                              <p className="text-xs text-muted-foreground mt-1">
                                {item.total_acertos} acertos
                              </p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 ml-2"
                              onClick={(e) => { e.stopPropagation(); handleExcluirDoRanking(item.participante_id, item.nome); }}
                              data-testid={`excluir-ranking-${item.participante_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {/* Barra de progresso visual */}
                          <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
                              style={{ width: `${Math.min(100, (item.total_pontos / (ranking[0]?.total_pontos || 1)) * 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Histórico de Batalhas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Swords className="w-5 h-5 text-red-500" />
                  Últimas Batalhas
                </CardTitle>
                <CardDescription>Histórico das competições recentes</CardDescription>
              </CardHeader>
              <CardContent>
                {historicoPartidas.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Swords className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    <p>Nenhuma batalha registrada</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {historicoPartidas.map((batalha, idx) => (
                      <div key={batalha.id || idx} className="p-4 bg-gradient-to-r from-slate-50 to-white rounded-lg border hover:shadow-md transition-all">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge className={
                              batalha.modo_batalha === 'aluno_vs_aluno' ? 'bg-blue-100 text-blue-700' :
                              batalha.modo_batalha === 'turma_vs_turma' ? 'bg-green-100 text-green-700' :
                              batalha.modo_batalha === 'time_vs_time' ? 'bg-purple-100 text-purple-700' :
                              'bg-orange-100 text-orange-700'
                            }>
                              {batalha.modo_batalha?.replace(/_/g, ' ')}
                            </Badge>
                            <Badge variant="outline">{batalha.disciplina}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {batalha.created_at ? new Date(batalha.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : ''}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => { e.stopPropagation(); handleExcluirBatalha(batalha.id); }}
                              data-testid={`excluir-batalha-${batalha.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                            <Crown className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-bold text-lg">{batalha.vencedor_nome}</p>
                            <p className="text-sm text-muted-foreground">Campeão da batalha</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground border-t pt-2 mt-2">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {batalha.participantes?.length || 0} competidores
                          </span>
                          <span className="flex items-center gap-1">
                            <Target className="w-4 h-4" />
                            {batalha.num_perguntas} perguntas
                          </span>
                          <span className="flex items-center gap-1">
                            <Timer className="w-4 h-4" />
                            {batalha.tempo_resposta}s/pergunta
                          </span>
                        </div>
                        
                        {/* Mini ranking da batalha */}
                        {batalha.participantes && batalha.participantes.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-muted-foreground mb-2">Resultado:</p>
                            <div className="flex flex-wrap gap-2">
                              {batalha.participantes
                                .sort((a, b) => (b.pontos || 0) - (a.pontos || 0))
                                .map((p, pIdx) => (
                                  <Badge 
                                    key={p.id} 
                                    variant={pIdx === 0 ? "default" : "outline"}
                                    className={pIdx === 0 ? "bg-yellow-500" : ""}
                                  >
                                    {pIdx === 0 ? '🏆 ' : ''}{p.nome}: {p.pontos?.toLocaleString('pt-BR')} pts
                                  </Badge>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal Criar Time */}
      <Dialog open={showAddTimeModal} onOpenChange={setShowAddTimeModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Criar Time</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome do Time</Label>
              <Input value={novoTime.nome} onChange={e => setNovoTime({...novoTime, nome: e.target.value})} placeholder="Ex: Os Invencíveis" />
            </div>
            <div className="space-y-2">
              <Label>Turma</Label>
              <Select value={novoTime.turma_id} onValueChange={v => { setNovoTime({...novoTime, turma_id: v, alunos: []}); carregarAlunosTurma(v); }}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {turmas.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {novoTime.turma_id && alunosPorTurma[novoTime.turma_id] && (
              <div className="space-y-2">
                <Label>Jogadores ({novoTime.alunos.length})</Label>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {alunosPorTurma[novoTime.turma_id].map(aluno => (
                    <div key={aluno.id} className="flex items-center gap-2 p-2 border-b last:border-0 hover:bg-slate-50">
                      <Checkbox checked={novoTime.alunos.includes(aluno.id)} onCheckedChange={() => toggleAlunoTime(aluno.id)} />
                      <span className="text-sm">{aluno.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTimeModal(false)}>Cancelar</Button>
            <Button onClick={adicionarTime} className="bg-purple-600 hover:bg-purple-700">Criar Time</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuizMilionario;
